import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import styles from './TRouteSidePanelActions.scss'
import {SHARE_MODAL} from 'common/modals/ShareModal'
import {REPLACE_ELEVATION_MODAL} from 'common/modals/ReplaceElevationModal'
import {PRINT_OFFICIAL_PDF_MODAL} from 'common/modals/PrintOfficialPDFModal'
import {PRINT_UPSELL_MODAL} from 'common/modals/PrintUpsellModal'
import {PRINT_LOGGED_OUT_MODAL} from 'common/modals/PrintLoggedOutModal'
import {DELETE_RECORD_MODAL} from 'common/modals/DeleteRecordModal'
import {EXPORT_TROUTE_MODAL} from 'common/modals/ExportTRouteModal'
import {EDIT_TROUTE_MODAL} from 'common/modals/EditTRouteModal'
import {COPY_TO_ROUTES_MODAL} from 'common/modals/CopyToRoutesModal'
import {showModal} from 'lib/modalUtils'
import {Icon} from 'common/Icon'
import {urls} from 'lib/urls'
import {currentURLWithQueryParams} from 'lib/utils'
import {favoriteSelectors, favoriteActions} from 'data/resources/favorites'
import {PopoverLinkMenu, PopoverLinkMenuItem as MenuItem, PopoverLinkMenuHeading as MenuHeading} from 'common/styleguide/PopoverLinkMenu'
import cx from 'classnames'

class EditButton extends React.PureComponent {
  render () {
    const {mini, troute, objectType} = this.props
    const editURL = urls[`${objectType}s`].edit(troute.id)

    // we display the edit button whenever the route can normally be edited by
    // the user, but we grey it out and add a tooltip when it's locked by the
    // parent org account.
    const displayEditButton = troute.permissions && troute.permissions.canBeModified
    if (!displayEditButton) { return null }
    const disableEditButton = troute.locked
    const EditIcon = <Fragment><Icon i='md-create' /> {!mini && 'Edit'}</Fragment>

    if (disableEditButton) {
      return <div style={{opacity: 0.4}} data-tip title='Editing is disabled because this route has been locked'>
        {EditIcon}
      </div>
    }

    if (objectType === 'route') {
      return (
        <a href={editURL}>
          {EditIcon}
        </a>
      )
    }

    return (
      <a onClick={() => showModal(EDIT_TROUTE_MODAL, {troute, objectType})}>
        {EditIcon}
      </a>
    )
  }
}

class _TRouteSidePanelActions extends React.PureComponent {
  state = {
    showMore: false
  }

  toggleMore = () => {
    this.setState({showMore: !this.state.showMore})
  }

  render () {
    const {troute, objectType, favorite, toggleFavorite, currentUser, mini} = this.props
    const {showMore} = this.state
    const shareModalProps = {
      objectType,
      objectId: troute.id,
      objectTitle: troute.name,
      objectUrl: urls[`${objectType}s`].show(troute.id, troute.privacyCode),
      privacyCode: troute.privacyCode
    }

    return (
      <div className={cx(styles.TRouteSidePanelActions, mini && styles.mini)}>
        <div onClick={toggleFavorite}>
          <Icon i='pin' data-tip title={mini && (favorite ? 'Pinned' : 'Pin')} /> {!mini && (favorite ? 'Pinned' : 'Pin')}
        </div>
        <div onClick={() => showModal(SHARE_MODAL, shareModalProps)}>
          <Icon i='md-share' /> {!mini && 'Share'}
        </div>
        {/*
        To make this work, I need to:
        - add the _permissions munging code to the Apiv3 helper so that I
          can access troute._permissions
        - handle the {can_modify: false}, {can_modify: true},
          and {can_modify: true, locked: true} states
        */}
        {<EditButton mini={mini} troute={troute} objectType={objectType} />}
        {!mini && <div ref={r => { this._more = r }} className={styles.more} onClick={this.toggleMore}>
          More <Icon i='ios-arrow-down' />
        </div>}
        {showMore && <MoreMenu
          target={this._more}
          troute={troute}
          objectType={objectType}
          currentUser={currentUser}
          onRequestClose={this.toggleMore} />}
      </div>
    )
  }
}

class MoreMenu extends React.PureComponent {
  showModal = (modal, props) => {
    showModal(modal, props)
    this.props.onRequestClose()
  }

  print = () => {
    const {troute, objectType, currentUser} = this.props

    switch (true) {
      case !!troute.officialMapPdfUrl:
        // if it's a club route with an official PDF, just show that
        this.showModal(PRINT_OFFICIAL_PDF_MODAL, {troute, objectType})
        return
      case !currentUser.id:
        // require login to print
        this.showModal(PRINT_LOGGED_OUT_MODAL)
        return
      case window.rwgps.config.privileges.printing:
        // if the user can fancy-print, do that
        window.open(`/${objectType}s/${troute.id}/print`)
        return
      default:
        // otherwise, upsell (which contains a link to starter print page)
        this.showModal(PRINT_UPSELL_MODAL, {troute, objectType})
    }
  }

  render () {
    const {objectType, target, troute, currentUser} = this.props
    // classic mode is the current URL, with a ?beta=false querystring param added
    const classicMode = currentURLWithQueryParams({beta: false})

    const editable = troute.permissions.canBeModified
    const locked = troute.locked

    return (
      <PopoverLinkMenu
        container={window.body}
        target={target}
        placement='bottom'
        onRequestClose={this.props.onRequestClose}>
        <MenuItem href={classicMode}>View in Classic Mode</MenuItem>
        <MenuItem onClick={() => this.showModal(EXPORT_TROUTE_MODAL, {troute, objectType})}>Export as File</MenuItem>
        <MenuItem onClick={this.print}>
          Print Map{objectType === 'route' && ' & Cue'} PDF
        </MenuItem>
        {objectType === 'route' && <MenuItem href={`/routes/${troute.id}/cue_sheet`} target='_blank'>Print Cuesheet</MenuItem>}
        {currentUser.id && troute.permissions.canBeCopied && <MenuItem onClick={() => this.showModal(COPY_TO_ROUTES_MODAL, {troute, objectType})}>
          Copy to My Routes
        </MenuItem>}
        {editable && <Fragment>
          <MenuHeading>Private Actions</MenuHeading>
          {!locked && <MenuItem onClick={() => this.showModal(REPLACE_ELEVATION_MODAL, {troute, objectType})}>Replace Elevation Data</MenuItem>}
          <MenuItem onClick={() => this.showModal(DELETE_RECORD_MODAL, {object: troute, objectType, objectName: troute.name})}>Delete</MenuItem>
        </Fragment>}
      </PopoverLinkMenu>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  favorite: favoriteSelectors.favoriteForObject(
    state,
    ownProps.objectType,
    ownProps.troute.id
  ),
  currentUser: state.currentUser
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  toggleFavorite: () => dispatch(
    favoriteActions.toggle(ownProps.objectType, ownProps.troute.id, 'web')
  )
})

export const TRouteSidePanelActions = connect(
  mapStateToProps,
  mapDispatchToProps
)(_TRouteSidePanelActions)
